import React, { FC, useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface CropModalProps {
  isOpen: boolean;
  imageSrc: string;
  onCancel: () => void;
  onConfirm: (croppedImageUrl: string) => void;
  loading: boolean;
}

export const CropModal: FC<CropModalProps> = ({ isOpen, imageSrc, onCancel, onConfirm, loading }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  // Reset crop state when image changes
  React.useEffect(() => {
    if (imageSrc) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [imageSrc]);

  const handleCropComplete = useCallback((_: any, pixels: any) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    
    try {
      const croppedImageUrl = await getCroppedImg(imageSrc, croppedAreaPixels);
      onConfirm(croppedImageUrl);
    } catch (error) {
      console.error('Error cropping image:', error);
    }
  };

  if (!isOpen) {
    return null;
  }
  
  // Use portal to render modal at document body level
  return createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        zIndex: 999999,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div 
        className="bg-black p-2 w-[98vw] max-w-[550px] flex flex-col items-center"
        style={{
          backgroundColor: 'black',
          padding: '8px',
          width: '98vw',
          maxWidth: '550px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 1000000
        }}
      >
        <div 
          className="relative w-full h-[55.125vw] max-h-[787.5px]"
          style={{
            position: 'relative',
            width: '100%',
            height: '55.125vw',
            maxHeight: '787.5px',
            backgroundColor: '#000'
          }}
        >
          {imageSrc && (
            <SimpleCropper
              imageSrc={imageSrc}
              crop={crop}
              zoom={zoom}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          )}
        </div>
        <div className="mt-4 flex" style={{ gap: '20px' }}>
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-500 text-white rounded"
            style={{
              padding: '8px 16px',
              backgroundColor: '#6b7280',
              color: 'white',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 text-white rounded disabled:opacity-50"
            style={{ 
              backgroundColor: '#10b981', 
              border: 'none',
              padding: '8px 16px',
              color: 'white',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1
            }}
          >
            {loading ? "Applying…" : "Apply Crop"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Simple cropper component (without external dependencies)
interface SimpleCropperProps {
  imageSrc: string;
  crop: { x: number; y: number };
  zoom: number;
  onCropChange: (crop: { x: number; y: number }) => void;
  onZoomChange: (zoom: number) => void;
  onCropComplete: (croppedArea: any, croppedAreaPixels: any) => void;
}

const SimpleCropper: FC<SimpleCropperProps> = ({
  imageSrc,
  crop,
  zoom,
  onCropChange,
  onZoomChange,
  onCropComplete
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageDimensions({ width: img.width, height: img.height });
      setImageLoaded(true);
    };
    img.src = imageSrc;
  }, [imageSrc]);

  useEffect(() => {
    if (imageLoaded && containerRef.current && imageRef.current) {
      // Calculate crop area and call onCropComplete
      const containerRect = containerRef.current.getBoundingClientRect();
      const imageRect = imageRef.current.getBoundingClientRect();
      
      const cropArea = {
        x: crop.x,
        y: crop.y,
        width: containerRect.width / zoom,
        height: containerRect.height / zoom
      };
      
      const cropAreaPixels = {
        x: (crop.x / containerRect.width) * imageDimensions.width,
        y: (crop.y / containerRect.height) * imageDimensions.height,
        width: (cropArea.width / containerRect.width) * imageDimensions.width,
        height: (cropArea.height / containerRect.height) * imageDimensions.height
      };
      
      onCropComplete(cropArea, cropAreaPixels);
    }
  }, [crop, zoom, imageLoaded, imageDimensions, onCropComplete]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: '#000'
      }}
    >
      {imageLoaded && (
        <img
          ref={imageRef}
          src={imageSrc}
          alt="Crop"
          style={{
            position: 'absolute',
            top: -crop.y,
            left: -crop.x,
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      )}
      
      {/* Crop overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          pointerEvents: 'none'
        }}
      />
      
      {/* Crop area */}
      <div
        style={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          right: '10%',
          bottom: '10%',
          border: '2px solid #fff',
          pointerEvents: 'none'
        }}
      />
    </div>
  );
};

// Helper function to crop image
const getCroppedImg = (imageSrc: string, croppedAreaPixels: any): Promise<string> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      // Use higher quality for better results, but still compressed for faster uploads
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(URL.createObjectURL(blob));
      }, 'image/jpeg', 0.92); // Increased quality from 0.8 to 0.92 for better visuals
    };
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = imageSrc;
  });
};

export default CropModal;
